import { useSelector } from "react-redux";
import type { RootState } from "../store";

/**
 * プロジェクト権限をチェックするカスタムフック
 */
export const useProjectPermission = () => {
  const { currentUser, projectMembers } = useSelector((state: RootState) => state.auth);

  /**
   * 現在のユーザーがプロジェクトでcontributorまたはownerの権限を持っているかチェック
   */
  const canAccessChat = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );

    return currentUserInProject?.role === "owner" || currentUserInProject?.role === "contributor";
  };

  /**
   * 現在のユーザーがプロジェクトでownerの権限を持っているかチェック
   */
  const canEditProject = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );

    return currentUserInProject?.role === "owner";
  };

  /**
   * 現在のユーザーがプロジェクトでドキュメント生成権限を持っているかチェック
   */
  const canGenerateDocuments = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );

    return currentUserInProject?.role === "owner" || currentUserInProject?.role === "contributor";
  };

  /**
   * 現在のユーザーがナレッジの作成・更新・削除権限を持っているかチェック
   * owner と contributor のみが可能
   */
  const canManageKnowledge = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );

    return currentUserInProject?.role === "owner" || currentUserInProject?.role === "contributor";
  };

  /**
   * 現在のユーザーがナレッジを表示できるかチェック
   * guest は表示不可、member 以上は表示可能
   */
  const canViewKnowledge = (): boolean => {
    if (!Array.isArray(projectMembers) || projectMembers.length === 0) {
      return false;
    }

    const currentUserInProject = projectMembers.find(
      (member) => member.user.id === currentUser?.id,
    );

    // guest は表示不可
    if (currentUserInProject?.role === "guest") {
      return false;
    }

    // member 以上は表示可能
    return (
      currentUserInProject?.role === "owner" ||
      currentUserInProject?.role === "contributor" ||
      currentUserInProject?.role === "member"
    );
  };

  return {
    canAccessChat,
    canEditProject,
    canGenerateDocuments,
    canManageKnowledge,
    canViewKnowledge,
  };
};
